import React, { useEffect, useState } from "react";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import { Card, notification, Popconfirm, Space, Spin, Tooltip } from "antd";
import { ListItem, ListItemAvatar, Typography } from "@mui/material";
import NoDataMessageComponent from "../../../components/generic/nodatamessage/NoDataMessageComponent";
import apiService from "../../../service/apiservice/apiService";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import CustomModal from "../../../components/generic/custommodal/CustomModal";
import SecondaryButton from "../../../components/generic/buttons/SecondaryButton";
import SmartIcon from "../../../components/generic/genericIcon/SmartIcon";
import AddEditFacilityForm from "./AddEditFacilityForm";

const FacilitiesSettings = () => {
  const organizationIdentifier = useGetOrgIdentifier();
  const [facilities, setFacilities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isModalVisible, setModalVisible] = useState(false);
  const [facilityDetails, setFacilityDetails] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [formAction, setFormAction] = useState("");

  const trimIfString = (value) =>
    typeof value === "string" ? value.trim() : value;

  const handleClick = async (e) => {
    e.preventDefault();
    setErrors(validate(facilityDetails));
    setIsSubmit(true);
  };
  const validate = (values) => {
    const errors = {};
    if (!trimIfString(values?.facilityName)) {
      errors.facilityName = "Name is required!";
    }
    if (!trimIfString(values?.completeAddress)) {
      errors.completeAddress = "Address is required!";
    }
    return errors;
  };

  const addClick = () => {
    setFacilityDetails(null);
    setFormAction("add");
    setModalVisible(true);
  };

  const editClick = async (id) => {
    const response = await apiService.get(
      `niramayafoundation/api/v1/facilities/${id}`
    );
    if (response?.facilityId) {
      setFacilityDetails({
        ...response,
        completeAddress: response?.facilityCompleteAddress,
      });
      setFormAction("edit");
      setModalVisible(true);
    }
  };

  const handleDelete = async (id) => {
    const response = await apiService.delete(
      `niramayafoundation/api/v1/facilities/${id}`
    );
    if (response?.message) {
      notification.success({
        message: response?.message,
        placement: "bottomRight",
      });
      getFacilities();
    } else {
      notification.error({
        message: "Error while deleting Facility",
        placement: "bottomRight",
      });
    }
  };

  const getFacilities = async () => {
    setIsLoading(true);
    const endpoint = "niramayafoundation/api/v1/facilities";
    const params = {
      facilityOrganizationId: organizationIdentifier,
    };
    const response = await apiService.get(endpoint, params);
    setFacilities(response);
    setIsLoading(false);
  };

  const handleSubmit = async () => {
    setSubmitLoading(true);
    const payload = {
      facilityOrganizationId: organizationIdentifier,
      facilityName: trimIfString(facilityDetails?.facilityName),
      facilityTypeId: facilityDetails?.facilityTypeId,
      facilityCompleteAddress: trimIfString(
        facilityDetails?.completeAddress ||
          facilityDetails?.facilityCompleteAddress,
      ),
      facilityStreetAddress: trimIfString(facilityDetails?.streetAddress),
      facilityAddressLocality: trimIfString(facilityDetails?.addressLocality),
      facilityAddressRegion: trimIfString(facilityDetails?.addressRegion),
      facilityPostalCode: trimIfString(facilityDetails?.postalCode),
      facilityPhone: trimIfString(facilityDetails?.facilityPhone),
      facilityLatitude: trimIfString(facilityDetails?.latitude),
      facilityLongitude: trimIfString(facilityDetails?.longitude),
      facilityComments: trimIfString(facilityDetails?.comments),
      facilityIsActive:
        formAction === "add" ? true : facilityDetails?.facilityIsActive,
    };

    const url =
      formAction === "edit"
        ? `niramayafoundation/api/v1/facilities/${facilityDetails?.facilityId}`
        : "niramayafoundation/api/v1/facilities";
    const method = formAction === "edit" ? "put" : "post";
    const successMessage =
      formAction === "edit"
        ? "Facility updated successfully"
        : "Facility added successfully";
    const errorMessage =
      formAction === "edit"
        ? "Error while updating facility"
        : "Error while adding facility";

    const response = await apiService[method](
      url,
      payload,
      successMessage,
      errorMessage
    );

    if (response?.facilityId) {
      setIsSubmit(false);
      setModalVisible(false);
      getFacilities();
      setFacilityDetails(null);
    }
    setSubmitLoading(false);
  };

  const customModelButtons = (
    <>
      <SecondaryButton
        buttonText={"Cancel"}
        handleOnClick={() => setModalVisible(false)}
      />
      <PrimaryButton
        buttonText={formAction === "edit" ? "Edit" : "Add"}
        handleOnClick={handleClick}
        loading={submitLoading}
      />
    </>
  );

  useEffect(() => {
    if (Object.keys(errors).length === 0 && isSubmit) {
      handleSubmit();
    }
  }, [errors, isSubmit]);

  useEffect(() => {
    getFacilities();
  }, [organizationIdentifier]);
  return (
    <>
      <Card
        className="services-card"
        title={
          <>
            <div className="preference-card-title">
              <strong className="services-card-title-font">Facilites</strong>
              <PrimaryButton
                buttonText="Create New Facility"
                handleOnClick={addClick}
              />
            </div>
          </>
        }
      >
        {isLoading ? (
          <Spin className="spinnerCenter" />
        ) : facilities && facilities?.length ? (
          facilities?.map((facility) => (
            <ListItem className="services-listitem" key={facility?.facilityId}>
              <Typography className="services-listitem-font">
                {facility?.facilityName}
              </Typography>
              <ListItemAvatar className="services-list-item-action">
                <Space size="middle">
                  <Tooltip title="Edit" placement="bottom">
                    <>
                      <SmartIcon
                        iconKey="edit"
                        width="20"
                        height="20"
                        handleIconClick={() => editClick(facility?.facilityId)}
                      />
                    </>
                  </Tooltip>
                  <Tooltip title="Delete" placement="bottom">
                    <>
                      <Popconfirm
                        title="Are you sure to delete this Facility?"
                        placement="left"
                        onConfirm={() => handleDelete(facility?.facilityId)}
                        okText="Confirm"
                        cancelText="Cancel"
                        overlayClassName="article-delete-popconfirm"
                      >
                        <>
                          <SmartIcon iconKey="delete" width="20" height="20" />
                        </>
                      </Popconfirm>
                    </>
                  </Tooltip>
                </Space>
              </ListItemAvatar>
            </ListItem>
          ))
        ) : (
          <NoDataMessageComponent text="No data available" />
        )}
      </Card>

      {isModalVisible && (
        <CustomModal
          isModalOpen={isModalVisible}
          footer={[customModelButtons]}
          closeModal={setModalVisible}
          title={
            formAction === "edit"
              ? "Edit Facility Details"
              : "Create New Facility"
          }
        >
          <AddEditFacilityForm
            facilityDetails={facilityDetails}
            setFacilityDetails={setFacilityDetails}
            errors={errors}
          />
        </CustomModal>
      )}
    </>
  );
};

export default FacilitiesSettings;
